import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, GraduationCap, MapPin,
  Globe, ArrowUpRight, Sparkles, Plus, Heart,
} from 'lucide-react';
import '../../components/ui/datatable.css';
import './master-dashboard.css';

// ─── Sub-componentes ───
import { EscolasPanel } from './components/EscolasPanel';
import { OnboardingChartsRow } from './components/OnboardingChartsRow';
import { HeatmapCard } from './components/HeatmapCard';
import { TrailConfigCard } from './components/TrailConfigCard';
import { AuditModal } from './components/AuditModal';
import { HierarquiaTree } from './components/HierarquiaTree';
import type { AuditModalState, GargaloItem, PieStatusItem, TrailStep, EvolucaoItem } from './components/master.types';
import { api } from '../../api/client';

// ─── Mini-componentes locais ───
function HeroStat({ icon: Icon, value, label, sub, color, onClick, onAdd, testId }: { icon: any; value: string | number; label: string; sub?: string; color: string; onClick?: () => void; onAdd?: () => void; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className={`master-hero-stat${onClick ? ' master-hero-stat--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{ background: `${color}30` }}
    >
      <div className="master-hero-stat-icon" style={{ background: color, color: '#fff' }}>
        <Icon size={14} strokeWidth={2.25} />
      </div>
      <div className="master-hero-stat-body">
        <div className="master-hero-stat-header">
          <span className="master-hero-stat-value">{value}</span>
          <span className="master-hero-stat-label">{label}</span>
        </div>
        {sub && <div className="master-hero-stat-sub" title={sub}>{sub}</div>}
      </div>
      {onAdd && (
        <button
          type="button"
          className="master-hero-stat-add"
          style={{ background: color }}
          onClick={e => { e.stopPropagation(); onAdd(); }}
          title={`Cadastrar novo`}
        >
          <Plus size={11} strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color, trend }: { icon: any; label: string; value: string | number; sub?: string; color: string; trend?: string }) {
  return (
    <div style={{ background: 'var(--bg-card, white)', borderRadius: 16, border: '1px solid var(--border-color, #e5e7eb)', padding: '18px 22px', position: 'relative', overflow: 'hidden', transition: 'transform .15s, box-shadow .15s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${color}06` }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}12`, color }}>
          <Icon size={18} />
        </div>
        <span style={{ fontSize: '.75rem', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #111)', marginBottom: 2 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 22 }}>
        {trend && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 8, background: '#10b98115', color: '#10b981', fontSize: '.7rem', fontWeight: 700 }}>
            <ArrowUpRight size={11} /> {trend}
          </span>
        )}
        {sub && <span style={{ fontSize: '.72rem', color: 'var(--text-muted, #9ca3af)' }}>{sub}</span>}
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, tone, onClick }: { icon: any; label: string; tone: 'blue' | 'teal' | 'green'; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`master-action-btn master-action-btn--${tone}`}>
      <Icon size={15} strokeWidth={2.25} />
      {label}
    </button>
  );
}

function buildWeekGrid(): { date: Date; days: number[] }[] {
  return Array.from({ length: 52 }).map((_, weekIndex) => {
    const date = new Date();
    date.setDate(date.getDate() - (51 - weekIndex) * 7);
    return { date, days: Array(7).fill(0) };
  });
}

/* ─── Dashboard Master ─── */
const AUDIT_MODAL_INITIAL: AuditModalState = {
  open: false, rawDate: '', dateLabel: '', total: 0,
  totalPages: 1, page: 1, logs: [], filtro: 'todos', loading: false,
};

const TRAIL_SEQUENCE = [
  { key: 'equipe', label: 'Equipe' },
  { key: 'turnos', label: 'Turnos' },
  { key: 'materias', label: 'Matérias' },
  { key: 'turmas', label: 'Turmas' },
  { key: 'professores', label: 'Professores' },
  { key: 'alunos', label: 'Alunos' },
  { key: 'grade', label: 'Grade' },
];

export function DashboardMaster() {
  const nav = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Queries React Query
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['master-dashboard'],
    queryFn: () => api.get('/master/dashboard').then(r => r.data),
    staleTime: 60000,
  });

  const { data: opsData, isLoading: loadingOps } = useQuery({
    queryKey: ['master-dashboard-operacional'],
    queryFn: () => api.get('/master/dashboard/operacional').then(r => r.data),
    staleTime: 60000,
  });

  // Heatmap
  const [heatmapFiltro, setHeatmapFiltro] = useState<'escolas' | 'todos' | 'usuarios'>('escolas');

  const { data: heatmapData, isLoading: heatmapLoading } = useQuery({
    queryKey: ['master-dashboard-heatmap', heatmapFiltro],
    queryFn: () => api.get('/master/dashboard/atividade-anual', { params: { filtro: heatmapFiltro } }).then(r => r.data),
    staleTime: 60000,
  });

  const heatmapWeeks = useMemo(() => {
    const porDia = heatmapData?.porDia || {};
    return buildWeekGrid().map(week => ({
      ...week,
      days: week.days.map((_, dayIndex) => {
        const d = new Date(week.date);
        d.setDate(d.getDate() + dayIndex);
        const key = d.toISOString().split('T')[0];
        const count = porDia[key] || 0;
        if (count === 0) return 0;
        if (count < 5) return 1;
        if (count < 15) return 2;
        if (count < 30) return 3;
        return 4;
      }),
    }));
  }, [heatmapData]);

  const loading = loadingStats || loadingOps;

  // Tempo mínimo de animação — dados carregam durante o splash
  // No embed do cockpit/simulador: sem delay nem fade de entrada
  const isCockpitEmbed = (() => {
    try {
      return sessionStorage.getItem('ac_cockpit_embed') === '1' || sessionStorage.getItem('ac_skip_splash') === '1';
    } catch {
      return false;
    }
  })();
  const [minTimePassed, setMinTimePassed] = useState(isCockpitEmbed);
  useEffect(() => {
    if (isCockpitEmbed) return;
    const t = setTimeout(() => setMinTimePassed(true), 800);
    return () => clearTimeout(t);
  }, [isCockpitEmbed]);

  const showLoading = !isCockpitEmbed && (loading || !minTimePassed);

  // Audit modal
  const [auditModal, setAuditModal] = useState<AuditModalState>(AUDIT_MODAL_INITIAL);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);

  // ─── API ───
  async function carregarAuditoriaDia(rawDate: string, page: number, filtro: 'todos' | 'escolas' | 'usuarios') {
    setAuditModal(prev => ({ ...prev, loading: true }));
    try {
      const { data: result } = await api.get('/master/dashboard/auditoria-dia', {
        params: { data: rawDate, page, limite: 100, filtro },
      });
      const dateLabel = new Date(rawDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      setAuditModal(prev => ({ ...prev, open: true, rawDate, dateLabel, total: result.total, totalPages: result.totalPages, page: result.page, logs: result.data, filtro, loading: false }));
    } catch (err) {
      console.warn('[DashboardMaster] Erro ao carregar auditoria do dia:', err);
      setAuditModal(prev => ({ ...prev, loading: false }));
    }
  }

  function handleHeatmapClick(date: Date, intensity: number) {
    if (intensity === 0) return;
    carregarAuditoriaDia(date.toISOString().split('T')[0], 1, auditModal.filtro);
  }

  // ─── Dados derivados ───
  const c = stats?.cards || {};
  const g = stats?.graficos || {};

  const totalAlunos = c.totalAlunosPrevistos ?? 0;
  const alunosAtivos = c.alunosAtivos ?? totalAlunos;
  const alunosInativos = c.alunosInativos ?? 0;

  const totalEstados = c.totalEstados ?? 0;
  const estadosAtivos = c.estadosAtivos ?? totalEstados;
  const estadosEmOperacao = c.estadosEmOperacao ?? 0;
  const estadosPendentes = c.estadosPendentes ?? 0;
  const estadosInativos = c.estadosInativos ?? 0;

  const totalMunicipios = c.totalMunicipios ?? 0;
  const municipiosAtivos = c.municipiosAtivos ?? totalMunicipios;
  const municipiosEmOperacao = c.municipiosEmOperacao ?? 0;
  const municipiosPendentes = c.municipiosPendentes ?? 0;
  const municipiosInativos = c.municipiosInativos ?? 0;

  const totalEscolas = c.totalEscolas ?? 0;
  const escolasAtivas = c.escolasAtivas ?? totalEscolas;
  const escolasEmAtividade = c.escolasEmAtividade ?? 0;
  const escolasEmImplantacao = c.escolasEmImplantacao ?? 0;
  const escolasInativas = c.escolasInativas ?? 0;

  const totalDiretores = c.totalDiretores ?? 0;
  const diretoresAtivos = c.diretoresAtivos ?? totalDiretores;
  const diretoresPendentes = c.diretoresPendentes ?? 0;
  const diretoresInativos = c.diretoresInativos ?? 0;

  const totalProfessores = c.totalProfessores ?? 0;
  const professoresAtivos = c.professoresAtivos ?? totalProfessores;
  const professoresPendentes = c.professoresPendentes ?? 0;
  const professoresInativos = c.professoresInativos ?? 0;

  const totalFamilias = c.totalFamilias ?? 0;
  const familiasAtivas = c.familiasAtivas ?? totalFamilias;
  const familiasInativas = c.familiasInativas ?? 0;

  const totalTurmas = c.totalTurmas ?? 0;
  const presentes = c.totalPresentes ?? 0;
  const atrasados = c.totalAtrasados ?? 0;
  const presencaMedia = totalAlunos > 0 ? Number((((presentes + atrasados) / totalAlunos) * 100).toFixed(1)) : 0;

  // Onboarding: status por escola
  let setupAguardando = 0, setupConcluido = 0, setupEmAndamento = 0;
  let setupAtrasado = 0, setupAbandonado = 0;
  const trailCount = { equipe: 0, turnos: 0, materias: 0, turmas: 0, professores: 0, alunos: 0, grade: 0 };
  const gargaloReal: Record<string, { count: number; escolas: string[] }> = {
    Equipe: { count: 0, escolas: [] }, Turnos: { count: 0, escolas: [] },
    'Matérias': { count: 0, escolas: [] }, Turmas: { count: 0, escolas: [] },
    Professores: { count: 0, escolas: [] }, Alunos: { count: 0, escolas: [] },
    Grade: { count: 0, escolas: [] },
  };

  const monitoradas = useMemo(() => {
    return (opsData?.ultimasEscolas || []).filter((e: any) =>
      e.ativo !== false &&
      e.isDemo !== true &&
      !e.nome?.toLowerCase().includes('demo')
    );
  }, [opsData?.ultimasEscolas]);

  monitoradas.forEach((esc: any) => {
    const sd = esc.setupData || {};
    const isWizardDone = esc.wizardConcluido === true;
    const isEquipeOk = (sd.equipe || 0) > 0 || isWizardDone || Boolean(esc.nomeDiretor || esc.diretorEscola);
    const isGradeOk = (sd.grade || 0) > 0 || isWizardDone || (Number(esc.wizardEtapaAtual || 0) >= 7);

    if (isEquipeOk) trailCount.equipe++;
    if ((sd.turnos || 0) > 0 || isWizardDone) trailCount.turnos++;
    if ((sd.materias || 0) > 0 || isWizardDone) trailCount.materias++;
    if ((sd.turmas || 0) > 0 || isWizardDone) trailCount.turmas++;
    if ((sd.professores || 0) > 0 || isWizardDone) trailCount.professores++;
    if ((sd.alunos || 0) > 0 || isWizardDone) trailCount.alunos++;
    if (isGradeOk) trailCount.grade++;

    const stepStatusMap: Record<string, boolean> = {
      equipe: isEquipeOk,
      turnos: (sd.turnos || 0) > 0 || isWizardDone,
      materias: (sd.materias || 0) > 0 || isWizardDone,
      turmas: (sd.turmas || 0) > 0 || isWizardDone,
      professores: (sd.professores || 0) > 0 || isWizardDone,
      alunos: (sd.alunos || 0) > 0 || isWizardDone,
      grade: isGradeOk,
    };

    const completedSteps = isWizardDone ? 7 : TRAIL_SEQUENCE.filter(s => stepStatusMap[s.key]).length;
    const pct = isWizardDone ? 100 : Math.round((completedSteps / 7) * 100);

    if (isWizardDone || pct === 100) {
      setupConcluido++;
    } else if (completedSteps === 0) {
      setupAguardando++;
    } else {
      setupEmAndamento++;
      const created = new Date(esc.createdAt || new Date()).getTime();
      const now = Date.now();
      const diasCriacao = (now - created) / (1000 * 60 * 60 * 24);
      let lastAccessDate = created;
      if (esc.ultimosAcessos?.length > 0) lastAccessDate = new Date(esc.ultimosAcessos[0].ultimoAcesso).getTime();
      const diasSemAcesso = (now - lastAccessDate) / (1000 * 60 * 60 * 24);
      if (diasSemAcesso > 7) setupAbandonado++;
      else if (diasCriacao > 3) setupAtrasado++;
    }

    if (!isWizardDone && completedSteps > 0 && completedSteps < 7) {
      const blocker = TRAIL_SEQUENCE.find(s => !stepStatusMap[s.key]);
      if (blocker && gargaloReal[blocker.label]) {
        gargaloReal[blocker.label].count++;
        gargaloReal[blocker.label].escolas.push(esc.nome || esc.id);
      }
    }
  });

  const totalEscolasOps = monitoradas.length;
  const pctAguardando = totalEscolasOps > 0 ? Math.round((setupAguardando / totalEscolasOps) * 100) : 0;

  const totaisRede = opsData?.totaisTrilha;

  const totalCadastros = {
    equipe: totaisRede?.equipe ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.equipe || 0), 0),
    turnos: totaisRede?.turnos ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.turnos || 0), 0),
    materias: totaisRede?.materias ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.materias || 0), 0),
    turmas: totaisRede?.turmas ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.turmas || 0), 0),
    professores: totaisRede?.professores ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.professores || 0), 0),
    alunos: totaisRede?.alunos ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.alunos || 0), 0),
    grade: totaisRede?.grade ?? monitoradas.reduce((acc: number, e: any) => acc + (e.setupData?.grade || 0), 0),
  };

  if (totalEscolasOps === 1 && monitoradas[0]?.setupData) {
    const sd = monitoradas[0].setupData;
    totalCadastros.equipe = sd.equipe || 0;
    totalCadastros.turnos = sd.turnos || 0;
    totalCadastros.materias = sd.materias || 0;
    totalCadastros.turmas = sd.turmas || 0;
    totalCadastros.professores = sd.professores || 0;
    totalCadastros.alunos = sd.alunos || 0;
    totalCadastros.grade = sd.grade || 0;
  }

  const GARGALOS_DATA: GargaloItem[] = Object.entries(gargaloReal)
    .map(([nome, { count, escolas }]) => ({ nome, faltam: count, escolas }))
    .filter(g => g.faltam > 0)
    .sort((a, b) => b.faltam - a.faltam);

  const PIE_STATUS: PieStatusItem[] = [
    { name: 'Aguardando', value: setupAguardando, color: '#94a3b8' },
    { name: 'Concluídas', value: setupConcluido, color: '#10b981' },
    { name: 'Em Configuração', value: setupEmAndamento, color: '#3b82f6' },
    { name: 'Atrasadas', value: setupAtrasado, color: '#f59e0b' },
    { name: 'Estagnadas', value: setupAbandonado, color: '#ef4444' },
  ];

  const TRAIL_STEPS: TrailStep[] = [
    {
      label: 'Equipe',
      count: trailCount.equipe,
      totalItens: totalCadastros.equipe,
      unidade: totalCadastros.equipe === 1 ? 'membro' : 'membros',
    },
    {
      label: 'Turnos',
      count: trailCount.turnos,
      totalItens: totalCadastros.turnos,
      unidade: totalCadastros.turnos === 1 ? 'turno' : 'turnos',
    },
    {
      label: 'Matérias',
      count: trailCount.materias,
      totalItens: totalCadastros.materias,
      unidade: totalCadastros.materias === 1 ? 'matéria' : 'matérias',
    },
    {
      label: 'Turmas',
      count: trailCount.turmas,
      totalItens: totalCadastros.turmas,
      unidade: totalCadastros.turmas === 1 ? 'turma' : 'turmas',
    },
    {
      label: 'Professores',
      count: trailCount.professores,
      totalItens: totalCadastros.professores,
      unidade: totalCadastros.professores === 1 ? 'professor' : 'professores',
    },
    {
      label: 'Alunos',
      count: trailCount.alunos,
      totalItens: totalCadastros.alunos,
      unidade: totalCadastros.alunos === 1 ? 'aluno' : 'alunos',
    },
    {
      label: 'Grade',
      count: trailCount.grade,
      totalItens: totalCadastros.grade,
      unidade: totalCadastros.grade === 1 ? 'aula' : 'aulas',
    },
  ];

  const evolucaoData: EvolucaoItem[] = useMemo(() => {
    if (opsData?.evolucaoOnboarding && Array.isArray(opsData.evolucaoOnboarding) && opsData.evolucaoOnboarding.length > 0) {
      return opsData.evolucaoOnboarding;
    }
    const escolas = monitoradas;
    const dias = 15;
    const now = new Date();
    return Array.from({ length: dias }).map((_, i) => {
      const targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (dias - 1 - i), 23, 59, 59, 999);
      const dia = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (dias - 1 - i)).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      if (escolas.length === 0) return { dia, emConfiguracao: 0, concluidas: 0 };

      const criadasAte = escolas.filter((e: any) => new Date(e.criadoEm || 0) <= targetDate);
      const concluidas = criadasAte.filter((e: any) => {
        const sd = e.setupData || {};
        const is100 = TRAIL_SEQUENCE.every(s => (sd[s.key] || 0) > 0);
        return e.wizardConcluido || is100;
      }).length;
      const emConfiguracao = Math.max(0, criadasAte.length - concluidas);

      return { dia, emConfiguracao, concluidas };
    });
  }, [opsData, monitoradas]);

  if (showLoading) return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: '4px solid var(--border-color, #e5e7eb)', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 14px' }} />
        <p style={{ color: 'var(--text-muted)' }}>Carregando Dashboard Master...</p>
      </div>
    </div>
  );

  return (
    <div data-testid="dashboard-master" className={`admin-page master-dashboard${isCockpitEmbed ? '' : ' animate-fadeIn'}`}>

      <header className="master-hero" data-tour="dash.master.hero">
        <div className="master-hero-bg" aria-hidden />
        <div className="master-hero-inner">
          <div className="master-hero-top">
            <div className="master-hero-copy">
              <div className="master-hero-eyebrow">
                <Sparkles size={14} style={{ color: '#fbbf24' }} />
                Aluno Conectado — Programa Escola Segura e Inteligente
              </div>
              <h1 className="master-hero-title" data-tour="dash.master.title">Painel Operacional</h1>
              <p className="master-hero-meta">
                Visão consolidada de toda a plataforma — {new Date().toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
          <div data-testid="master-hero-stats" className="master-hero-stats" aria-label="Indicadores da plataforma" data-tour="dash.master.kpis">
            <HeroStat
              testId="stat-estados"
              icon={Globe}
              value={totalEstados}
              label="Estados"
              sub={estadosInativos > 0 ? `${estadosAtivos} ativos • ${estadosInativos} inat.` : `${estadosAtivos} ativos`}
              color="#2563eb"
              onClick={() => nav('/master/estados')}
              onAdd={() => nav('/master/estados', { state: { openNew: true } })}
            />
            <HeroStat
              testId="stat-municipios"
              icon={MapPin}
              value={totalMunicipios}
              label="Municípios"
              sub={
                (municipiosPendentes > 0 || municipiosInativos > 0)
                  ? `${municipiosEmOperacao} oper. • ${municipiosPendentes} pend.${municipiosInativos > 0 ? ` • ${municipiosInativos} inat.` : ''}`
                  : `${municipiosAtivos} ativos`
              }
              color="#0891b2"
              onClick={() => nav('/master/municipios')}
              onAdd={() => nav('/master/municipios', { state: { openNew: true } })}
            />
            <HeroStat
              testId="stat-escolas"
              icon={Building2}
              value={totalEscolas}
              label="Escolas"
              sub={
                (escolasEmImplantacao > 0 || escolasInativas > 0)
                  ? `${escolasEmAtividade} ativ. • ${escolasEmImplantacao} implant.${escolasInativas > 0 ? ` • ${escolasInativas} inat.` : ''}`
                  : `${escolasAtivas} ativas`
              }
              color="#10b981"
              onClick={() => nav('/master/escolas')}
              onAdd={() => nav('/master/escolas', { state: { openNew: true } })}
            />
            <HeroStat
              testId="stat-diretores"
              icon={Building2}
              value={totalDiretores}
              label="Diretores"
              sub={
                diretoresPendentes > 0 || diretoresInativos > 0
                  ? `${diretoresAtivos} ativ. • ${diretoresPendentes} conv.${diretoresInativos > 0 ? ` • ${diretoresInativos} inat.` : ''}`
                  : `${diretoresAtivos} ativos`
              }
              color="#059669"
              onClick={() => nav('/master/usuarios', { state: { filtro: 'DIRETOR' } })}
              onAdd={() => nav('/master/usuarios', { state: { openInvite: true, perfil: 'ESCOLA' } })}
            />
            <HeroStat
              testId="stat-professores"
              icon={Users}
              value={totalProfessores}
              label="Professores"
              sub={
                professoresPendentes > 0 || professoresInativos > 0
                  ? `${professoresAtivos} ativ. • ${professoresPendentes} conv.${professoresInativos > 0 ? ` • ${professoresInativos} inat.` : ''}`
                  : `${professoresAtivos} ativos`
              }
              color="#3b82f6"
              onClick={() => nav('/master/usuarios', { state: { filtro: 'PROFESSOR' } })}
              onAdd={() => nav('/master/usuarios', { state: { openInvite: true, perfil: 'PROFESSOR' } })}
            />
            <HeroStat
              testId="stat-alunos"
              icon={GraduationCap}
              value={totalAlunos.toLocaleString('pt-BR')}
              label="Alunos"
              sub={alunosInativos > 0 ? `${alunosAtivos} cad. • ${alunosInativos} inat.` : 'Cad. escolar'}
              color="#6366f1"
              onClick={() => nav('/master/usuarios', { state: { filtro: 'ALUNO' } })}
            />
            <HeroStat
              testId="stat-responsaveis"
              icon={Heart}
              value={totalFamilias}
              label="Responsáveis"
              sub={familiasInativas > 0 ? `${familiasAtivas} ativ. • ${familiasInativas} inat.` : 'Cad. e convites'}
              color="#ec4899"
              onClick={() => nav('/master/usuarios', { state: { filtro: 'RESPONSAVEL' } })}
              onAdd={() => nav('/master/usuarios', { state: { openInvite: true, perfil: 'RESPONSAVEL' } })}
            />
          </div>
        </div>
      </header>

      <div className="master-shell">
        <div className="master-main">
          <EscolasPanel
            ultimasEscolas={monitoradas}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            totalMunicipios={totalMunicipios}
          />

          {totalEscolas > 0 && (
          <div className="master-analytics">
            <div className="master-charts-slot">
              <OnboardingChartsRow
                evolucaoData={evolucaoData}
                pieStatus={PIE_STATUS}
                gargalosData={GARGALOS_DATA}
                setupEmAndamento={setupEmAndamento}
                setupConcluido={setupConcluido}
                totalEscolas={totalEscolas}
              />
            </div>
            <div className="master-status-grid">
              <HeatmapCard
                heatmapFiltro={heatmapFiltro}
                setHeatmapFiltro={setHeatmapFiltro}
                heatmapWeeks={heatmapWeeks}
                heatmapLoading={heatmapLoading}
                onDayClick={handleHeatmapClick}
              />
              <TrailConfigCard
                totalEscolas={totalEscolasOps}
                setupAguardando={setupAguardando}
                pctAguardando={pctAguardando}
                trailSteps={TRAIL_STEPS}
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ═══ AUDIT MODAL ═══ */}
      <AuditModal
        auditModal={auditModal}
        onClose={() => setAuditModal(prev => ({ ...prev, open: false }))}
        onFilter={carregarAuditoriaDia}
        selectedLog={selectedLog}
        setSelectedLog={setSelectedLog}
      />

    </div>
  );
}
